import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, boolean, timestamp, jsonb, numeric, unique, index } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import type { z } from "zod";

 
import { tenants, users, companies } from './common';
import { workers } from './workers';

export type PartnerId = string & { readonly __brand: "PartnerId" };

export const partners = pgTable("partners", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`).$type<PartnerId>(),
  name: text("name").notNull(),
  legalOrganizationId: text("legal_organization_id").references(() => companies.id),
  lifecycleStatus: text("lifecycle_status").notNull().default("draft"),
  marketplaceStatus: text("marketplace_status").notNull().default("unlisted"),
  complianceBaseline: jsonb("compliance_baseline").$type<Record<string, any>>().default({}),
  notes: text("notes"),
  contactEmail: text("contact_email"),
  website: text("website"),
  industry: text("industry"),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertPartnerSchema = createInsertSchema(partners).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPartner = z.infer<typeof insertPartnerSchema>;
export type Partner = typeof partners.$inferSelect;

export const tenantPartnerLinks = pgTable("tenant_partner_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  partnerId: text("partner_id").notNull().references(() => partners.id),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  linkStatus: text("link_status").notNull().default("pending"),
  accessProfileSlug: text("access_profile_slug").references(() => partnerAccessProfiles.companyType),
  participationRoleTypeId: text("participation_role_type_id").references(() => participationRoleTypes.id),
  subscriptionId: text("subscription_id").references(() => partnerSubscriptions.id),
  complianceStatus: text("compliance_status").notNull().default("pending"),
  complianceOverrides: jsonb("compliance_overrides").$type<Record<string, any>>().default({}),
  linkedAt: timestamp("linked_at", { withTimezone: true }),
  linkedBy: text("linked_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  uniqPartnerTenant: unique("tenant_partner_links_partner_id_tenant_id_key").on(t.partnerId, t.tenantId),
  idxTplPartner: index("idx_tpl_partner_id").on(t.partnerId),
  idxTplTenant: index("idx_tpl_tenant_id").on(t.tenantId),
  idxTplStatus: index("idx_tpl_link_status").on(t.linkStatus),
}));
export const insertTenantPartnerLinkSchema = createInsertSchema(tenantPartnerLinks).omit({ id: true, updatedAt: true });
export type InsertTenantPartnerLink = z.infer<typeof insertTenantPartnerLinkSchema>;
export type TenantPartnerLink = typeof tenantPartnerLinks.$inferSelect;

export const partnerAuditLog = pgTable("partner_audit_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  partnerId: text("partner_id").notNull().references(() => partners.id),
  tenantId: text("tenant_id"),
  eventType: text("event_type").notNull(),
  actorId: text("actor_id"),
  actorName: text("actor_name"),
  previousState: jsonb("previous_state").$type<Record<string, any>>(),
  newState: jsonb("new_state").$type<Record<string, any>>(),
  notes: text("notes"),
  ipAddress: text("ip_address"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxPalPartner: index("idx_pal_partner_id").on(t.partnerId),
  idxPalEventType: index("idx_pal_event_type").on(t.eventType),
}));
export const insertPartnerAuditLogSchema = createInsertSchema(partnerAuditLog).omit({ id: true, createdAt: true });
export type InsertPartnerAuditLog = z.infer<typeof insertPartnerAuditLogSchema>;
export type PartnerAuditLogEntry = typeof partnerAuditLog.$inferSelect;

export const participationRoleTypes = pgTable("participation_role_types", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  code: text("code").notNull().unique(),
  name: text("name").notNull(),
  description: text("description"),
  canDeployWorkers: boolean("can_deploy_workers").notNull().default(false),
  canCreatePtw: boolean("can_create_ptw").notNull().default(false),
  canApprovePtw: boolean("can_approve_ptw").notNull().default(false),
  canOwnInvestigation: boolean("can_own_investigation").notNull().default(false),
  canIssueCredentials: boolean("can_issue_credentials").notNull().default(false),
  canAuditOnly: boolean("can_audit_only").notNull().default(false),
  // Flags migrated from companies table (operational, tenant-scoped)
  canRequestPtw: boolean("can_request_ptw").notNull().default(false),
  canSuperviseWork: boolean("can_supervise_work").notNull().default(false),
  canIssuePermits: boolean("can_issue_permits").notNull().default(false),
  canApproveAccess: boolean("can_approve_access").notNull().default(false),
  // New capability flags
  canVerifyWorkerIdentity: boolean("can_verify_worker_identity").notNull().default(false),
  canReportIncidents: boolean("can_report_incidents").notNull().default(true),
  canRaiseNonConformances: boolean("can_raise_non_conformances").notNull().default(false),
  canApproveTimesheets: boolean("can_approve_timesheets").notNull().default(false),
  canManageEquipment: boolean("can_manage_equipment").notNull().default(false),
  isAuditOnly: boolean("is_audit_only").notNull().default(false),
  accessTier: text("access_tier").notNull().default("worker"),
  ptwRolesGranted: text("ptw_roles_granted").array().default([]),
  isBuiltIn: boolean("is_built_in").notNull().default(false),
  status: text("status").notNull().default("active"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertParticipationRoleTypeSchema = createInsertSchema(participationRoleTypes).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertParticipationRoleType = z.infer<typeof insertParticipationRoleTypeSchema>;
export type ParticipationRoleType = typeof participationRoleTypes.$inferSelect;

// ─── Company–Tenancy Participations ──────────────────────────────────
export const partnerAccessProfiles = pgTable("partner_access_profiles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyType: text("company_type").notNull(),
  canDeployWorkers: boolean("can_deploy_workers").notNull().default(false),
  canCreatePtw: boolean("can_create_ptw").notNull().default(false),
  canApprovePtw: boolean("can_approve_ptw").notNull().default(false),
  canOwnInvestigation: boolean("can_own_investigation").notNull().default(false),
  canIssueCredentials: boolean("can_issue_credentials").notNull().default(false),
  canAuditOnly: boolean("can_audit_only").notNull().default(false),
  canRequestPtw: boolean("can_request_ptw").notNull().default(false),
  canSuperviseWork: boolean("can_supervise_work").notNull().default(false),
  canIssuePermits: boolean("can_issue_permits").notNull().default(false),
  canApproveAccess: boolean("can_approve_access").notNull().default(false),
  canVerifyWorkerIdentity: boolean("can_verify_worker_identity").notNull().default(false),
  canReportIncidents: boolean("can_report_incidents").notNull().default(false),
  canRaiseNonConformances: boolean("can_raise_non_conformances").notNull().default(false),
  canApproveTimesheets: boolean("can_approve_timesheets").notNull().default(false),
  canManageEquipment: boolean("can_manage_equipment").notNull().default(false),
  isAuditOnly: boolean("is_audit_only").notNull().default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  uniqCompanyType: unique("partner_access_profiles_company_type_unique").on(t.companyType),
}));
export const insertPartnerAccessProfileSchema = createInsertSchema(partnerAccessProfiles).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPartnerAccessProfile = z.infer<typeof insertPartnerAccessProfileSchema>;
export type PartnerAccessProfile = typeof partnerAccessProfiles.$inferSelect;

// Partner Plans
export const partnerPlans = pgTable("partner_plans", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  slug: text("slug").notNull().unique(),
  description: text("description"),
  isActive: boolean("is_active").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertPartnerPlanSchema = createInsertSchema(partnerPlans).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPartnerPlan = z.infer<typeof insertPartnerPlanSchema>;
export type PartnerPlan = typeof partnerPlans.$inferSelect;

// Partner Plan Entitlements: key-value pairs per plan
export const partnerPlanEntitlements = pgTable("partner_plan_entitlements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  planId: text("plan_id").notNull().references(() => partnerPlans.id, { onDelete: "cascade" }),
  entitlementKey: text("entitlement_key").notNull(),
  booleanValue: boolean("boolean_value"),
  numericLimit: integer("numeric_limit"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  uniqEntitlementKeyPlan: unique("partner_plan_entitlements_entitlement_key_plan_id_unique").on(t.entitlementKey, t.planId),
}));
export const insertPartnerPlanEntitlementSchema = createInsertSchema(partnerPlanEntitlements).omit({ id: true, createdAt: true });
export type InsertPartnerPlanEntitlement = z.infer<typeof insertPartnerPlanEntitlementSchema>;
export type PartnerPlanEntitlement = typeof partnerPlanEntitlements.$inferSelect;

// Partner Subscriptions: company → plan assignment
export const partnerSubscriptions = pgTable("partner_subscriptions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: text("company_id").notNull().unique("partner_subscriptions_company_id_key").references(() => companies.id),
  planId: text("plan_id").notNull().references(() => partnerPlans.id),
  status: text("status").notNull().default("active"),
  trialEnd: timestamp("trial_end", { withTimezone: true }),
  currentPeriodEnd: timestamp("current_period_end", { withTimezone: true }),
  externalSubscriptionId: text("external_subscription_id"),
  notes: text("notes"),
  assignedBy: text("assigned_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertPartnerSubscriptionSchema = createInsertSchema(partnerSubscriptions).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPartnerSubscription = z.infer<typeof insertPartnerSubscriptionSchema>;
export type PartnerSubscription = typeof partnerSubscriptions.$inferSelect;

// Partner Billing Events: fire-and-forget audit trail
export const partnerBillingEvents = pgTable("partner_billing_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  eventType: text("event_type").notNull(),
  companyId: text("company_id").notNull().references(() => companies.id),
  tenantId: text("tenant_id").references(() => tenants.id),
  metadata: jsonb("metadata").$type<Record<string, any>>().default({}),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxCompanyEventType: index("partner_billing_events_company_id_event_type_idx").on(t.companyId, t.eventType),
}));
export const insertPartnerBillingEventSchema = createInsertSchema(partnerBillingEvents).omit({ id: true, createdAt: true });
export type InsertPartnerBillingEvent = z.infer<typeof insertPartnerBillingEventSchema>;
export type PartnerBillingEvent = typeof partnerBillingEvents.$inferSelect;

// Partner Usage Snapshots: daily usage counts per company
// Pack 6 (Task #1052): bump whenever the columns above change so readers
// can refuse to trust pre-bump rows during sensitive ops like company merge.
export const PARTNER_USAGE_SNAPSHOT_SCHEMA_VERSION = 1;

export const partnerUsageSnapshots = pgTable("partner_usage_snapshots", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: text("company_id").notNull().references(() => companies.id),
  snapshotDate: text("snapshot_date").notNull(),
  workerCount: integer("worker_count").notNull().default(0),
  tenantConnectionCount: integer("tenant_connection_count").notNull().default(0),
  userCount: integer("user_count").notNull().default(0),
  evidenceDocumentCount: integer("evidence_document_count").notNull().default(0),
  permitCount: integer("permit_count").notNull().default(0),
  incidentCount: integer("incident_count").notNull().default(0),
  schemaVersion: integer("schema_version").notNull().default(1),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  companyDateUnique: unique("partner_usage_snapshots_company_date").on(t.companyId, t.snapshotDate),
  // Pack 6 (Task #1052): used by snapshot-trust gate to skip stale rows
  // whose schemaVersion < PARTNER_USAGE_SNAPSHOT_SCHEMA_VERSION.
  idxSchemaVersion: index("idx_partner_usage_snapshots_schema_version").on(t.schemaVersion),
}));
export const insertPartnerUsageSnapshotSchema = createInsertSchema(partnerUsageSnapshots).omit({ id: true, createdAt: true });
export type InsertPartnerUsageSnapshot = z.infer<typeof insertPartnerUsageSnapshotSchema>;
export type PartnerUsageSnapshot = typeof partnerUsageSnapshots.$inferSelect;

// Partner Access Requests: partner → tenant connection requests
export const partnerAccessRequests = pgTable("partner_access_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: text("company_id").notNull().references(() => companies.id),
  targetTenantId: text("target_tenant_id").notNull().references(() => tenants.id),
  status: text("status").notNull().default("pending"),
  requestedBy: text("requested_by").references(() => users.id),
  reviewedBy: text("reviewed_by").references(() => users.id),
  reviewedAt: timestamp("reviewed_at", { withTimezone: true }),
  rejectionReason: text("rejection_reason"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertPartnerAccessRequestSchema = createInsertSchema(partnerAccessRequests).omit({ id: true, createdAt: true, updatedAt: true, reviewedAt: true });
export type InsertPartnerAccessRequest = z.infer<typeof insertPartnerAccessRequestSchema>;
export type PartnerAccessRequest = typeof partnerAccessRequests.$inferSelect;

// Company Invitations: invite colleagues to a partner company portal
export const marketplaceListings = pgTable("marketplace_listings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: text("company_id").notNull().unique().references(() => companies.id),
  headline: text("headline"),
  description: text("description"),
  specialisations: text("specialisations").array(),
  geographicCoverage: text("geographic_coverage").array(),
  minComplianceScore: integer("min_compliance_score"),
  isVerified: boolean("is_verified").notNull().default(false),
  isFeatured: boolean("is_featured").notNull().default(false),
  isPublished: boolean("is_published").notNull().default(false),
  viewsCount: integer("views_count").notNull().default(0),
  inquiriesCount: integer("inquiries_count").notNull().default(0),
  publishedAt: timestamp("published_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertMarketplaceListingSchema = createInsertSchema(marketplaceListings).omit({ id: true, createdAt: true, updatedAt: true, publishedAt: true });
export type InsertMarketplaceListing = z.infer<typeof insertMarketplaceListingSchema>;
export type MarketplaceListing = typeof marketplaceListings.$inferSelect;

// Commercial Agreements: contracts between partner companies and tenants
export const commercialAgreements = pgTable("commercial_agreements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: text("company_id").notNull().references(() => companies.id),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  title: text("title").notNull(),
  agreementType: text("agreement_type").notNull().default("service_contract"),
  status: text("status").notNull().default("draft"),
  startDate: text("start_date"),
  endDate: text("end_date"),
  value: numeric("value"),
  currency: text("currency").default("USD"),
  documentUrl: text("document_url"),
  signedAt: timestamp("signed_at", { withTimezone: true }),
  signedByPartner: text("signed_by_partner"),
  signedByTenant: text("signed_by_tenant"),
  notes: text("notes"),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertCommercialAgreementSchema = createInsertSchema(commercialAgreements).omit({ id: true, createdAt: true, updatedAt: true, signedAt: true });
export type InsertCommercialAgreement = z.infer<typeof insertCommercialAgreementSchema>;
export type CommercialAgreement = typeof commercialAgreements.$inferSelect;

// Partner Portal Preferences: per-company portal UI prefs
export const partnerPortalPreferences = pgTable("partner_portal_preferences", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: text("company_id").notNull().references(() => companies.id).unique(),
  defaultView: text("default_view").default("dashboard"),
  notificationsEnabled: boolean("notifications_enabled").notNull().default(true),
  // NOTE: customPortalName and customLogoUrl live inside `preferences` JSONB
  // because partner_portal_preferences hit PostgreSQL's 1600 physical attribute limit.
  preferences: jsonb("preferences").$type<Record<string, any>>().default({}),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertPartnerPortalPreferencesSchema = createInsertSchema(partnerPortalPreferences).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPartnerPortalPreferences = z.infer<typeof insertPartnerPortalPreferencesSchema>;
export type PartnerPortalPreferences = typeof partnerPortalPreferences.$inferSelect;



// ─── SMS Audit Log ────────────────────────────────────────────────────────────
export const partnerAgreements = pgTable("partner_agreements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  title: text("title").notNull(),
  agreementType: text("agreement_type").notNull().default("service_contract"),
  parties: jsonb("parties").$type<string[]>().default([]),
  status: text("status").notNull().default("draft"),
  value: text("value"),
  currency: text("currency").notNull().default("USD"),
  startDate: text("start_date"),
  endDate: text("end_date"),
  documentUrl: text("document_url"),
  signedAt: text("signed_at"),
  notes: text("notes"),
  companyId: text("company_id").references(() => companies.id),
  tenantId: text("tenant_id").references(() => tenants.id),
  createdBy: text("created_by").references(() => users.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertPartnerAgreementSchema = createInsertSchema(partnerAgreements).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPartnerAgreement = z.infer<typeof insertPartnerAgreementSchema>;
export type PartnerAgreement = typeof partnerAgreements.$inferSelect;

// ─── Partner API Keys ────────────────────────────────────────────────────────
// Super-admin managed API keys issued to partner organizations (not user PATs).
// Token value shown once at creation, stored as a masked prefix + hash.
export const partnerApiKeys = pgTable("partner_api_keys", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  partnerId: text("partner_id").notNull().references(() => partners.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  tokenPrefix: text("token_prefix").notNull(),
  tokenHash: text("token_hash").notNull(),
  scopes: text("scopes").array().notNull().default([]),
  isActive: boolean("is_active").notNull().default(true),
  createdBy: text("created_by").references(() => users.id),
  lastUsedAt: timestamp("last_used_at", { withTimezone: true }),
  expiresAt: timestamp("expires_at", { withTimezone: true }),
  revokedAt: timestamp("revoked_at", { withTimezone: true }),
  revokedBy: text("revoked_by").references(() => users.id),
  revokeReason: text("revoke_reason"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => ({
  partnerIdIdx: index("idx_partner_api_keys_partner_id").on(table.partnerId),
  isActiveIdx: index("partner_api_keys_is_active_idx").on(table.isActive),
  tokenHashUniq: unique("partner_api_keys_token_hash_key").on(table.tokenHash),
}));
export const insertPartnerApiKeySchema = createInsertSchema(partnerApiKeys).omit({ id: true, tokenHash: true, lastUsedAt: true, revokedAt: true, createdAt: true });
export type InsertPartnerApiKey = z.infer<typeof insertPartnerApiKeySchema>;
export type PartnerApiKey = typeof partnerApiKeys.$inferSelect;

// ─── Identity Verifications ──────────────────────────────────────────────────
// Records of worker identity checks performed by partner companies.
export const identityVerifications = pgTable("identity_verifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull().references(() => workers.id),
  verifiedByCompanyId: text("verified_by_company_id").notNull().references(() => companies.id),
  verifiedByUserId: text("verified_by_user_id").references(() => users.id),
  verificationMethod: text("verification_method").notNull().default("manual"),
  verificationStatus: text("verification_status").notNull().default("verified"),
  notes: text("notes"),
  evidenceDocumentIds: text("evidence_document_ids").array().default([]),
  verificationEvidence: jsonb("verification_evidence").$type<Record<string, any>>().default({}),
  verifiedAt: timestamp("verified_at", { withTimezone: true }).defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxIvTenant: index("idx_iv_tenant_id").on(t.tenantId),
  idxIvWorker: index("idx_iv_worker_id").on(t.workerId),
  idxIvCompany: index("idx_iv_company_id").on(t.verifiedByCompanyId),
}));
export const insertIdentityVerificationSchema = createInsertSchema(identityVerifications).omit({ id: true, createdAt: true, verifiedAt: true });
export type InsertIdentityVerification = z.infer<typeof insertIdentityVerificationSchema>;
export type IdentityVerification = typeof identityVerifications.$inferSelect;

// ─── Global Roles ─────────────────────────────────────────────────────────────
// Platform-wide named roles with permission bundles managed by super-admin.
// Built-in roles (is_built_in=true) cannot be deleted.
